export const mention = (state, node) => {
  const uri = state.esc(
    `mention://user/${node.attrs.userId}/${encodeURIComponent(
      node.attrs.userFullName
    )}`
  );
  const escapedDisplayName = state.esc('@' + (node.attrs.userFullName || ''));

  state.write(`[${escapedDisplayName}](${uri})`);
};

export const blockquote = (state, node) => {
  state.wrapBlock('> ', null, node, () => state.renderContent(node));
};
export const code_block = (state, node) => {
  state.write('```' + (node.attrs.params || '') + '\n');
  state.text(node.textContent, false);
  state.ensureNewLine();
  state.write('```');
  state.closeBlock(node);
};
export const heading = (state, node) => {
  state.write(state.repeat('#', node.attrs.level) + ' ');
  state.renderInline(node);
  state.closeBlock(node);
};
export const horizontal_rule = (state, node) => {
  state.write(node.attrs.markup || '---');
  state.closeBlock(node);
};
export const bullet_list = (state, node) => {
  const isRTL = node.attrs.dir === 'rtl';
  if (isRTL) state.write('[bulletListRTL]\n');
  state.renderList(node, '  ', () => (node.attrs.bullet || '*') + ' ');
  if (isRTL) {
    state.ensureNewLine();
    state.write('[/bulletListRTL]');
    state.closeBlock(node);
  }
};
export const ordered_list = (state, node) => {
  const isRTL = node.attrs.dir === 'rtl';
  if (isRTL) state.write('[orderedListRTL]\n');
  let start = node.attrs.order || 1;
  let maxW = String(start + node.childCount - 1).length;
  let space = state.repeat(' ', maxW + 2);
  state.renderList(node, space, i => {
    let nStr = String(start + i);
    return state.repeat(' ', maxW - nStr.length) + nStr + '. ';
  });
  if (isRTL) {
    state.ensureNewLine();
    state.write('[/orderedListRTL]');
    state.closeBlock(node);
  }
};
export const list_item = (state, node) => {
  state.renderContent(node);
};
export const paragraph = (state, node) => {
  const { align = 'left', direction = 'ltr', autoDetected } = node.attrs;

  if (node.content.size === 0) {
    // Empty paragraphs (blank lines the user inserted between paragraphs)
    // serialize to nothing in CommonMark, so a non-breaking space keeps
    // the blank paragraph from being collapsed away when re-rendered.
    state.write(' ');
    state.closeBlock(node);
    return;
  }

  const isRTLRight = align === 'right' && direction === 'rtl';
  const isManualLTRLeft =
    autoDetected === false && align === 'left' && direction === 'ltr';

  let alignmentTag = null;
  if (isRTLRight) {
    alignmentTag = 'alignRightRTL';
  } else if (isManualLTRLeft) {
    alignmentTag = 'alignLeftLTR';
  } else if (align && align !== 'left') {
    alignmentTag = `align${align.charAt(0).toUpperCase()}${align.slice(1)}`;
  }

  if (alignmentTag) {
    state.write(`[${alignmentTag}]`);
    state.renderInline(node);
    state.write(`[/${alignmentTag}]`);
    state.closeBlock(node);
  } else {
    state.renderInline(node);
    state.closeBlock(node);
  }
};
export const image = (state, node) => {
  let src = state.esc(node.attrs.src);
  if (node.attrs.height) {
    const param = `cw_image_height=${node.attrs.height}`;
    if (src.includes('?')) {
      src = src.includes('cw_image_height=')
        ? src.replace(/cw_image_height=[^&]+/, param)
        : `${src}&${param}`;
    } else {
      src += `?${param}`;
    }
  }
  state.write(
    '![' +
      state.esc(node.attrs.alt || '') +
      '](' +
      src +
      (node.attrs.title ? ' ' + state.quote(node.attrs.title) : '') +
      ')'
  );
};
export const html_block = (state, node) => {
  state.write(node.attrs.html || '');
  state.closeBlock(node);
};
export const hard_break = (state, node, parent, index) => {
  for (let i = index + 1; i < parent.childCount; i++)
    if (parent.child(i).type !== node.type) {
      state.write('  \n');
      return;
    }
};
export const text = (state, node) => {
  state.text(node.text, false);
};

export const em = {
  open: '*',
  close: '*',
  mixable: true,
  expelEnclosingWhitespace: true,
};
export const superscript = {
  open: '^',
  close: '^',
  mixable: false,
  escape: false,
  expelEnclosingWhitespace: false,
};
export const strike = {
  open: '~~',
  close: '~~',
  mixable: true,
  expelEnclosingWhitespace: true,
};
export const strong = {
  open: '**',
  close: '**',
  mixable: true,
  expelEnclosingWhitespace: true,
};
export const fontSize = {
  open(state, mark) {
    return `[fontSize=${mark.attrs.size}]`;
  },
  close: '[/fontSize]',
  mixable: true,
  expelEnclosingWhitespace: true,
};
export const fontFamily = {
  open(state, mark) {
    return `[fontFamily=${mark.attrs.family}]`;
  },
  close: '[/fontFamily]',
  mixable: true,
  expelEnclosingWhitespace: true,
};
export const textColor = {
  open(state, mark) {
    return `[textColor=${mark.attrs.color}]`;
  },
  close: '[/textColor]',
  mixable: true,
  expelEnclosingWhitespace: true,
};
export const link = {
  open(_state, mark, parent, index) {
    return isPlainURL(mark, parent, index, 1) ? '<' : '[';
  },
  close(state, mark, parent, index) {
    return isPlainURL(mark, parent, index, -1)
      ? '>'
      : '](' +
          state.esc(mark.attrs.href) +
          (mark.attrs.title ? ' ' + state.quote(mark.attrs.title) : '') +
          ')';
  },
  escape: false,
};
export const code = {
  open(_state, _mark, parent, index) {
    return backticksFor(parent.child(index), -1);
  },
  close(_state, _mark, parent, index) {
    return backticksFor(parent.child(index - 1), 1);
  },
  escape: false,
};

function backticksFor(node, side) {
  let ticks = /`+/g;
  let m;
  let len = 0;
  if (node.isText)
    while ((m = ticks.exec(node.text))) len = Math.max(len, m[0].length);
  let result = len > 0 && side > 0 ? ' `' : '`';
  for (let i = 0; i < len; i++) result += '`';
  if (len > 0 && side < 0) result += ' ';
  return result;
}

function isPlainURL(link, parent, index, side) {
  if (link.attrs.title || !/^\w+:/.test(link.attrs.href)) return false;
  let content = parent.child(index + (side < 0 ? -1 : 0));
  if (
    !content.isText ||
    content.text != link.attrs.href ||
    content.marks[content.marks.length - 1] != link
  )
    return false;
  if (index == (side < 0 ? 1 : parent.childCount - 1)) return true;
  let next = parent.child(index + (side < 0 ? -2 : 1));
  return !link.isInSet(next.marks);
}
